import React from 'react';
import type { ReactNode } from 'react';
import type { Channel } from '../../types/channel';
import type { ChannelRowDnd } from './ChannelList';

const TypeIcon = ({ type }: { type?: string }) => {
  switch (type) {
    case 'voice': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M11 5a3 3 0 0 1 6 0v7a3 3 0 0 1-6 0Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><path d="M12 19v3"/></svg>;
    case 'announcement': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M3 11v2"/><path d="M7 11v2"/><path d="M21 8a8.1 8.1 0 0 1-3.6 6.7L12 18v-6l5.4-2.7A8.1 8.1 0 0 0 21 8Z"/><path d="M12 18H7a3 3 0 0 1-3-3"/></svg>;
    case 'kanban': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect x="3" y="4" width="6" height="16" rx="1"/><rect x="15" y="4" width="6" height="10" rx="1"/></svg>;
    case 'form': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h10"/></svg>;
    case 'habit': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M9 14l2 2 4-4"/></svg>;
    case 'gallery': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L9 18"/></svg>;
    case 'kitchen': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 2v20"/><path d="M5 7h14"/><path d="M19 2H5c0 4-1 5-1 9a8 8 0 0 0 16 0c0-4-1-5-1-9Z"/><path d="M8 2v4"/><path d="M16 2v4"/></svg>;
    case 'calendar': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M8 2v4"/><path d="M16 2v4"/><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/></svg>;
    case 'notes': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>;
    case 'challenge': return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    default: return <span className="text-[0.875rem] leading-none">#</span>;
  }
};

const EMPTY_USERS: string[] = [];

// Memoized so navigating channels (which re-renders the whole sidebar) doesn't
// re-render — or flicker — the voice roster. It only updates when the actual
// roster (`users`) or the name map changes, both of which are referentially
// stable across channel navigation.
const VoiceMembers = React.memo(function VoiceMembers({ users, nameById, names: given, max = 6 }: { users: string[]; nameById: Map<string, string>; names?: string[]; max?: number }) {
  if (users.length === 0) return null;
  const names = given ?? users.map((uid) => nameById.get(uid) || uid.slice(0, 8));
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <ul className="ml-6 mt-1 space-y-0.5 text-meta text-emerald-200/80">
      {shown.map((name, idx) => (
        <li key={idx} className="flex items-center gap-1.5 truncate">
          <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-emerald-400/70" />
          <span className="truncate">{name}</span>
        </li>
      ))}
      {extra > 0 && (
        <li className="flex items-center gap-1.5 truncate text-emerald-300/70">
          <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-emerald-400/50" />
          <span className="truncate">+{extra} more</span>
        </li>
      )}
    </ul>
  );
});

/**
 * One channel in the channel column. The desktop sidebar and the phone's channel drawer both render this, so the two
 * look and behave the same: tap to open, press and drag to reorder (see ChannelList for the drag wiring).
 */
export function ChannelRow({
  channel,
  active,
  unread,
  dnd,
  onSelect,
  voiceUsers = EMPTY_USERS,
  voiceNameById,
  voiceNames,
  actions,
}: {
  channel: Channel;
  active: boolean;
  /** Shows the unread dot. */
  unread: boolean;
  dnd: ChannelRowDnd;
  onSelect: () => void;
  /** People in this voice channel: ids plus a name lookup, or already-resolved names. */
  voiceUsers?: string[];
  voiceNameById?: Map<string, string>;
  voiceNames?: string[];
  /** Extra controls shown on hover at the row's right edge (the desktop options button). */
  actions?: ReactNode;
}) {
  return (
    <div
      ref={dnd.wrapperRef}
      style={dnd.wrapperStyle}
      className={`relative group ${dnd.dragging ? 'rounded-md bg-panel-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] ring-2 ring-emerald-500/60' : ''}`}
    >
      <button
        {...dnd.buttonProps}
        className={`w-full text-left px-2 min-h-8 rounded-md transition-colors hover:bg-neutral-800/70 flex items-center gap-2.5 text-[0.8125rem] leading-tight ${active ? 'bg-white/[0.06] text-fg' : 'text-fg-2'}`}
        data-active-channel={active ? 'true' : undefined}
        aria-roledescription="reorderable channel"
        onClick={() => { if (dnd.consumeClick()) return; onSelect(); }}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-3">
          <TypeIcon type={channel.type} />
        </span>
        <span className="inline-flex items-center gap-1 truncate">
          <span className="truncate">{channel.name}</span>
        </span>
        {unread && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle"></span>}
      </button>
      {channel.type === 'voice' && (
        <VoiceMembers users={voiceUsers} nameById={voiceNameById ?? new Map()} names={voiceNames} />
      )}
      {actions}
    </div>
  );
}
